import {
  mapRepresentativesToUpdateDto,
  mapRepresentativesToUpdateDtoForGroupPatch,
  asPersistedContractEntityId,
} from '../services/contracts/buildContractUpdatePayload';
import type { UpdateContractRepresentativeDto } from '../services/contracts/ContractService';
import { cleanCpf, normalizeRepresentativePhoneInput } from '../services/utils/stringUtils';
import {
  matchLineModalRepresentativeEditKey,
  mergeLineModalRepresentatives,
  type LineModalRepresentativeLike,
} from './lineModalRepresentativesUtils';

export type RepresentativeInlineEditData = {
  nome: string;
  cpf: string;
  telefone: string;
  email: string;
};

export async function resolveRepresentativesForInlineSave(
  priorReps: LineModalRepresentativeLike[],
  contractId: string,
  fetchPersistedRepresentatives: (id: string) => Promise<LineModalRepresentativeLike[]>
): Promise<LineModalRepresentativeLike[]> {
  const needsPersistedIds = priorReps.some((r) => !asPersistedContractEntityId(r.id));
  if (!needsPersistedIds) {
    return priorReps;
  }
  const persisted = await fetchPersistedRepresentatives(contractId);
  return mergeLineModalRepresentatives(priorReps, persisted);
}

export function applyInlineRepresentativeEdit(
  reps: LineModalRepresentativeLike[],
  editingRepInModal: string,
  editingRepData: RepresentativeInlineEditData
): LineModalRepresentativeLike[] {
  const emailTrim = (editingRepData.email || '').trim();
  const telNorm = normalizeRepresentativePhoneInput(editingRepData.telefone || '');
  const nomeRep = editingRepData.nome.trim();
  const cpfRep = cleanCpf(editingRepData.cpf);

  return reps.map((r, index) =>
    matchLineModalRepresentativeEditKey(r, index, editingRepInModal)
      ? {
          ...r,
          nome: nomeRep,
          cpf: cpfRep,
          telefone: telNorm,
          email: emailTrim,
        }
      : r
  );
}

export function buildInlineRepresentativePatchPayloads(updatedReps: LineModalRepresentativeLike[]): {
  solo: UpdateContractRepresentativeDto[];
  group: UpdateContractRepresentativeDto[];
} {
  return {
    solo: mapRepresentativesToUpdateDto(updatedReps),
    group: mapRepresentativesToUpdateDtoForGroupPatch(updatedReps),
  };
}

export type InlineRepresentativeSaveDeps = {
  fetchPersistedRepresentatives: (contractId: string) => Promise<LineModalRepresentativeLike[]>;
  getContractById: (contractId: string) => Promise<{ updatedAt?: string | Date | null }>;
  getContractsByGroupId: (groupId: string) => Promise<Array<{ id: string; updatedAt?: string | Date | null }>>;
  patchContract: (contractId: string, data: Record<string, unknown>) => Promise<void>;
};

export type InlineRepresentativeSaveContext = {
  contractId: string;
  groupId?: string | null;
  representatives: LineModalRepresentativeLike[];
  editingRepInModal: string;
  editingRepData: RepresentativeInlineEditData;
};

export type InlineRepresentativeSaveResult = {
  updatedRepresentatives: LineModalRepresentativeLike[];
  updatedAt: string;
  /** Preenchido quando save em contrato de grupo (mensagem de sucesso). */
  groupContractCount?: number;
};

function toIsoUpdatedAt(value?: string | Date | null): string | undefined {
  if (!value) return undefined;
  return new Date(value).toISOString();
}

/**
 * Persiste QSA inline (solo ou grupo). Lança em falha de PATCH — o caller mantém estado anterior.
 */
export async function runInlineRepresentativeSave(
  context: InlineRepresentativeSaveContext,
  deps: InlineRepresentativeSaveDeps
): Promise<InlineRepresentativeSaveResult> {
  const repsWithIds = await resolveRepresentativesForInlineSave(
    context.representatives,
    context.contractId,
    deps.fetchPersistedRepresentatives
  );

  const updatedReps = applyInlineRepresentativeEdit(
    repsWithIds,
    context.editingRepInModal,
    context.editingRepData
  );

  const { solo: repsPayloadSolo, group: repsPayloadGroup } =
    buildInlineRepresentativePatchPayloads(updatedReps);

  if (context.groupId) {
    const groupContracts = await deps.getContractsByGroupId(context.groupId);
    for (const c of groupContracts) {
      await deps.patchContract(c.id, {
        updatedAt: toIsoUpdatedAt(c.updatedAt),
        representatives: repsPayloadGroup,
      });
    }
    return {
      updatedRepresentatives: updatedReps,
      updatedAt: new Date().toISOString(),
      groupContractCount: groupContracts.length,
    };
  }

  const freshContract = await deps.getContractById(context.contractId);
  await deps.patchContract(context.contractId, {
    updatedAt: toIsoUpdatedAt(freshContract.updatedAt),
    representatives: repsPayloadSolo,
  });

  return {
    updatedRepresentatives: updatedReps,
    updatedAt: freshContract.updatedAt
      ? toIsoUpdatedAt(freshContract.updatedAt)!
      : new Date().toISOString(),
  };
}
